import type { AssignedRatingDraft, Catalog, ComorbidityRule, MemberAssignedRating, MemberWithRatings, RatingSource, SyntheticRatingSource } from "../types";
import { DIABETES_MELLITUS_CONDITION_ID, HYPERTENSION_CONDITION_ID, SYNTHETIC_RATING_SOURCES } from "../types";
import { activeModificationFor, effectiveRatingDebits } from "./debits";

export const DOUBLED_BUILD_RATING_TEXT = "Doubled Build Points";
export const DOUBLED_HYPERTENSION_RATING_TEXT = "Doubled Hypertension";

export function isSyntheticSource(source: string): source is SyntheticRatingSource {
  return (SYNTHETIC_RATING_SOURCES as readonly string[]).includes(source);
}

function realActiveRatings(member: MemberWithRatings): MemberAssignedRating[] {
  return member.assignedRatings.filter((rating) => !rating.isExcluded && !isSyntheticSource(rating.source));
}

function conditionNameOf(conditionId: number, catalog: Catalog, fallback: string): string {
  return catalog.conditions.find((condition) => condition.id === conditionId)?.name ?? fallback;
}

function syntheticDraft(member: MemberWithRatings, conditionId: number, catalog: Catalog, source: RatingSource, ratingText: string, debits: number): AssignedRatingDraft {
  return {
    memberId: member.id,
    ratingId: null,
    conditionId,
    conditionName: conditionNameOf(conditionId, catalog, ratingText),
    ratingText,
    time: null,
    defaultDebits: debits,
    assignedDebits: debits,
    isMedication: false,
    uwDollars: null,
    isExcluded: false,
    irixSource: false,
    notes: null,
    source,
    changeReason: null,
    changeExplanation: null,
    wasOnSkynetOriginal: false,
    substitutedFromRatingId: null,
  };
}

/**
 * Doubled Build (product plan 4.3): when the member has a non-excluded rating
 * under a RequiresDoubleBuild condition and build points are above 0, one
 * synthetic row equal to build sits under the first such condition by assignment order.
 */
export function doubledBuildRow(member: MemberWithRatings, build: number, catalog: Catalog): AssignedRatingDraft | null {
  if (build <= 0) return null;
  const doubling = new Set(catalog.conditions.filter((condition) => condition.requiresDoubleBuild).map((condition) => condition.id));
  const trigger = realActiveRatings(member).find((rating) => doubling.has(rating.conditionId));
  if (!trigger) return null;
  return syntheticDraft(member, trigger.conditionId, catalog, "DoubledBuild", DOUBLED_BUILD_RATING_TEXT, build);
}

/**
 * Doubled Hypertension (product plan 4.4): with both Diabetes Mellitus and
 * Hypertension present, add the sum of non-medication Hypertension debits.
 */
export function doubledHypertensionRow(member: MemberWithRatings, catalog: Catalog): AssignedRatingDraft | null {
  const active = realActiveRatings(member);
  const hasDiabetes = active.some((rating) => rating.conditionId === DIABETES_MELLITUS_CONDITION_ID);
  const hypertension = active.filter((rating) => rating.conditionId === HYPERTENSION_CONDITION_ID);
  if (!hasDiabetes || hypertension.length === 0) return null;
  const debits = hypertension.filter((rating) => !rating.isMedication).reduce((sum, rating) => sum + rating.assignedDebits, 0);
  return syntheticDraft(member, HYPERTENSION_CONDITION_ID, catalog, "DoubledHypertension", DOUBLED_HYPERTENSION_RATING_TEXT, debits);
}

/**
 * Comorbidity auto-adds (product plan 4.11): for each active rule whose trigger
 * matches a non-excluded rating, add the companion rating unless the member
 * already has any rating under the companion's condition.
 */
export function comorbidityRows(member: MemberWithRatings, rules: readonly ComorbidityRule[], catalog: Catalog): AssignedRatingDraft[] {
  const active = realActiveRatings(member);
  const drafts: AssignedRatingDraft[] = [];
  for (const rule of rules) {
    if (!rule.isActive) continue;
    const triggered = active.some((rating) => (rule.triggerRatingId !== null ? rating.ratingId === rule.triggerRatingId : rating.conditionId === rule.triggerConditionId));
    if (!triggered) continue;
    const addRating = catalog.ratings.find((rating) => rating.id === rule.addRatingId);
    if (!addRating) continue;
    const alreadyCovered = member.assignedRatings.some((rating) => rating.conditionId === addRating.conditionId) || drafts.some((draft) => draft.conditionId === addRating.conditionId);
    if (alreadyCovered) continue;
    const debits = effectiveRatingDebits(addRating, activeModificationFor(addRating.id, catalog.modifications));
    drafts.push({
      ...syntheticDraft(member, addRating.conditionId, catalog, "Comorbidity", addRating.ratingText, debits),
      ratingId: addRating.id,
      time: addRating.time,
      isMedication: addRating.isMedication,
      notes: rule.notes,
    });
  }
  return drafts;
}

export interface SyntheticReconciliation {
  member: MemberWithRatings;
  added: readonly AssignedRatingDraft[];
  removedIds: readonly string[];
}

interface ReconcileOptions {
  newId: () => string;
  nowUtc: string;
}

function reconcileOne(member: MemberWithRatings, source: SyntheticRatingSource, desired: AssignedRatingDraft | null, options: ReconcileOptions): SyntheticReconciliation {
  const existing = member.assignedRatings.find((rating) => rating.source === source);
  if (!desired) {
    const assignedRatings = member.assignedRatings.filter((rating) => rating.source !== source);
    return { member: { ...member, assignedRatings }, added: [], removedIds: existing ? [existing.id] : [] };
  }
  if (!existing) {
    const created: MemberAssignedRating = { ...desired, id: options.newId(), createdUtc: options.nowUtc, modifiedUtc: options.nowUtc };
    return { member: { ...member, assignedRatings: [...member.assignedRatings, created] }, added: [desired], removedIds: [] };
  }
  const wasOverridden = existing.assignedDebits !== existing.defaultDebits;
  const updated: MemberAssignedRating = {
    ...existing,
    conditionId: desired.conditionId,
    conditionName: desired.conditionName,
    defaultDebits: desired.defaultDebits,
    assignedDebits: wasOverridden ? existing.assignedDebits : desired.assignedDebits,
  };
  const changed = updated.conditionId !== existing.conditionId || updated.defaultDebits !== existing.defaultDebits || updated.assignedDebits !== existing.assignedDebits;
  const assignedRatings = member.assignedRatings.map((rating) => (rating.id === existing.id ? (changed ? { ...updated, modifiedUtc: options.nowUtc } : rating) : rating));
  return { member: { ...member, assignedRatings }, added: [], removedIds: [] };
}

/**
 * Brings the member's synthetic rows in line with the engine: Doubled Build and
 * Doubled Hypertension are added, updated, or removed as their triggers change.
 * Overridden synthetic debits and exclusions are kept while the trigger holds. Engine rows are never
 * removed by hand; an underwriter excludes one instead, and it leaves when its trigger clears.
 */
export function reconcileSyntheticRows(member: MemberWithRatings, build: number, catalog: Catalog, options: ReconcileOptions): SyntheticReconciliation {
  const afterBuild = reconcileOne(member, "DoubledBuild", doubledBuildRow(member, build, catalog), options);
  const afterHypertension = reconcileOne(afterBuild.member, "DoubledHypertension", doubledHypertensionRow(afterBuild.member, catalog), options);
  return {
    member: afterHypertension.member,
    added: [...afterBuild.added, ...afterHypertension.added],
    removedIds: [...afterBuild.removedIds, ...afterHypertension.removedIds],
  };
}
